import { Helmet } from "react-helmet";
import { useQuery } from "@apollo/client";
import { RequestFailed } from "src/components/RequestFailed";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ControlledDataTable } from "@/components/ui/controlled-data-table";
import { Skeleton } from "@chakra-ui/react";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button as UIButton } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  communityJobOpportunitiesColumns,
} from "@/features/communities/columns";
import {
  GET_COMMUNITY_JOB_OPPORTUNITIES,
} from "src/features/communities/graphql";

export default function CommunityOpportunities() {
  const {
    loading: loadingOpportunities,
    data: opportunitiesData,
    error: opportunitiesError,
  } = useQuery(GET_COMMUNITY_JOB_OPPORTUNITIES, {
    fetchPolicy: "network-only",
  });

  const opportunities =
    opportunitiesData?.getCommunityJobOpportunitiesForPro || [];

  const opportunitiesTable = useReactTable({
    data: opportunities,
    columns: communityJobOpportunitiesColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (opportunitiesError) {
    return <RequestFailed text={opportunitiesError.message} />;
  }

  return (
    <>
      <Helmet>
        <title>LaborHack | Community Opportunities</title>
      </Helmet>
      <main className="flex flex-col gap-8">
        <div className="flex flex-col gap-3 lg:gap-6">
          <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-4 border-b pb-2">
            <h1 className="text-[18px] lg:text-[23px] font-semibold">
              Community opportunities
            </h1>
          </div>

          {/* Job Opportunities Section */}
          <div className="">
            <h2 className="text-[15px] lg:text-[18px] font-semibold mb-4">
              Job opportunities
            </h2>
            <div className="flow-root">
              {loadingOpportunities ? (
                <div className="rounded-md border">
                  <div className="p-4 space-y-4">
                    {/* Header skeleton */}
                    <div className="flex border-b pb-4">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} className="h-5 w-1/6 mr-4" />
                      ))}
                    </div>
                    {/* Rows skeleton */}
                    {Array(5)
                      .fill(0)
                      .map((_, index) => (
                        <div key={index} className="flex py-3">
                          {[1, 2, 3, 4, 5, 6].map((i) => (
                            <Skeleton key={i} className="h-5 w-1/6 mr-4" />
                          ))}
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <>
                  <ControlledDataTable table={opportunitiesTable} />
                  <div className="flex flex-col">
                    <Separator />
                    <div className="w-full min-h-16">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 py-4">
                        <p className="flex items-center gap-2 text-sm text-gray-500">
                          {opportunitiesTable.getRowCount() === 0 ? (
                            "Showing no opportunities"
                          ) : (
                            <>
                              Showing
                              <span className="text-sm text-[#121212]">
                                {opportunitiesTable.getState().pagination
                                  .pageIndex *
                                  opportunitiesTable.getState().pagination
                                    .pageSize +
                                  1}
                                -
                                {Math.min(
                                  (opportunitiesTable.getState().pagination
                                    .pageIndex +
                                    1) *
                                    opportunitiesTable.getState().pagination
                                      .pageSize,
                                  opportunitiesTable.getFilteredRowModel().rows
                                    .length
                                )}
                              </span>
                              of{" "}
                              <span className="text-sm text-[#121212]">
                                {opportunitiesTable.getRowCount()}
                              </span>
                              opportunities
                            </>
                          )}
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center space-x-2">
                            <UIButton
                              className={cn(
                                "rounded-full px-[9px] border-[#212121]",
                                !opportunitiesTable.getCanPreviousPage() &&
                                  "border-[#D4D4D4]"
                              )}
                              variant="outline"
                              size="sm"
                              onClick={() => opportunitiesTable.previousPage()}
                              disabled={
                                !opportunitiesTable.getCanPreviousPage()
                              }
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </UIButton>
                            <UIButton
                              className={cn(
                                "rounded-full px-[9px] border-[#212121]",
                                !opportunitiesTable.getCanNextPage() &&
                                  "border-[#D4D4D4]"
                              )}
                              variant="outline"
                              size="sm"
                              onClick={() => opportunitiesTable.nextPage()}
                              disabled={!opportunitiesTable.getCanNextPage()}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </UIButton>
                          </div>
                          <Select
                            value={String(
                              opportunitiesTable.getState().pagination.pageSize
                            )}
                            onValueChange={(value) => {
                              opportunitiesTable.setPageSize(Number(value));
                            }}
                          >
                            <SelectTrigger className="w-[70px]">
                              <SelectValue placeholder="10" />
                            </SelectTrigger>
                            <SelectContent>
                              {[5, 10, 20, 50].map((pageSize) => (
                                <SelectItem
                                  key={pageSize}
                                  value={String(pageSize)}
                                >
                                  {pageSize}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
